import { defineStore } from 'pinia';
import { ref } from 'vue';
import { OrgAddressService } from '@/services/orgAddress.services';
import type {
  IOrganisationListAddress,
  ICreateOrgAddressBody,
  IUpdateOrgAddressBody,
} from '@/types/manageOrgCommerce.types';

/**
 * Store Pinia pour la gestion des adresses d'une organisation.
 */
export const useOrgAddressStore = defineStore('orgAddress', () => {
  const addresses = ref<IOrganisationListAddress[]>([]);
  const currentAddress = ref<IOrganisationListAddress | null>(null);

  /**
   * Crée une adresse pour une organisation.
   * @param organizationId - UUID de l'organisation
   * @param data - Corps de la requête
   */
  const createOrgAddress = async (
    organizationId: string,
    data: ICreateOrgAddressBody,
  ): Promise<IOrganisationListAddress> => {
    try {
      const response = await OrgAddressService.createOrgAddress(
        organizationId,
        data,
      );
      const address = (response.data as any)?.data ?? response.data;
      addresses.value.push(address);
      return address;
    } catch (error) {
      return Promise.reject(error);
    }
  };

  /**
   * Liste les adresses d'une organisation.
   * @param organizationId - UUID de l'organisation
   */
  const searchOrgAddresses = async (
    organizationId: string,
  ): Promise<IOrganisationListAddress[]> => {
    try {
      const response =
        await OrgAddressService.searchOrgAddresses(organizationId);
      const list: IOrganisationListAddress[] = Array.isArray(response.data)
        ? response.data
        : ((response.data as any)?.data ?? []);
      addresses.value = list;
      return list;
    } catch (error) {
      return Promise.reject(error);
    }
  };

  /**
   * Récupère une adresse par son ID.
   * @param organizationId - UUID de l'organisation
   * @param addressId - UUID de l'adresse
   */
  const getOrgAddressById = async (
    organizationId: string,
    addressId: string,
  ): Promise<IOrganisationListAddress> => {
    try {
      const response = await OrgAddressService.getOrgAddressById(
        organizationId,
        addressId,
      );
      currentAddress.value = response.data;
      return response.data;
    } catch (error) {
      return Promise.reject(error);
    }
  };

  /**
   * Met à jour une adresse d'organisation.
   * @param organizationId - UUID de l'organisation
   * @param addressId - UUID de l'adresse
   * @param data - Corps de la requête
   */
  const updateOrgAddress = async (
    organizationId: string,
    addressId: string,
    data: IUpdateOrgAddressBody,
  ): Promise<IOrganisationListAddress> => {
    try {
      const response = await OrgAddressService.updateOrgAddress(
        organizationId,
        addressId,
        data,
      );
      const updated = (response.data as any)?.data ?? response.data;
      const idx = addresses.value.findIndex((a) => a.id === addressId);
      if (idx !== -1) addresses.value[idx] = updated;
      return updated;
    } catch (error) {
      return Promise.reject(error);
    }
  };

  /**
   * Supprime une adresse d'organisation.
   * @param organizationId - UUID de l'organisation
   * @param addressId - UUID de l'adresse
   */
  const deleteOrgAddress = async (
    organizationId: string,
    addressId: string,
  ): Promise<void> => {
    try {
      await OrgAddressService.deleteOrgAddress(organizationId, addressId);
      addresses.value = addresses.value.filter((a) => a.id !== addressId);
    } catch (error) {
      return Promise.reject(error);
    }
  };

  return {
    addresses,
    currentAddress,
    createOrgAddress,
    searchOrgAddresses,
    getOrgAddressById,
    updateOrgAddress,
    deleteOrgAddress,
  };
});
